import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5 } from "styled-components";
import { Wrap } from "~/features/ui/wrap";
import { type Mode } from "~/features/ui/get-theme-style";
import { Headline } from "~/features/ui/headline";
import { takeFirst } from "~/features/app/utils";
import { Button } from "~/features/ui/button";
import { Link } from "~/features/ui/link";
import { isLinkValid } from "~/features/ui/link/utils";
import { ScrollHighlightText } from "~/features/ui/scroll-highlight-text";
import { RichText } from "~/features/ui/rich-text";
import { Asset, type Props as AssetProps } from "~/features/craft/asset";
import ArrowRight from "~/public/icons/arrow-right.svg";
import ArrowTopRight from "~/public/icons/arrow-top-right.svg";
type PlainLink = Record<string, unknown> & {
  href?: string;
  newWindow?: boolean;
  text?: React.ReactNode;
};
type PlainAsset = Record<string, unknown>;
type SectionDataAttrs = Record<`data-${string}`, string | undefined>;
function getPlainLinkIcon(link?: PlainLink | null) {
  if (!link) {
    return undefined;
  }
  return link.newWindow ? ArrowTopRight : ArrowRight;
}
export type HighlightHeadlineBlockViewProps = {
  anchor?: Array<{
    id?: string | null;
    name?: string | null;
  }> | {
    id?: string | null;
    name?: string | null;
  } | null;
  hasScrollHighlighting?: boolean | null;
  hasSolidBackground?: boolean | null;
  hasTopDivider?: boolean | null;
  headline?: string | null;
  image?: PlainAsset | null;
  isBig?: boolean | null;
  isHighlighted?: boolean | null;
  link?: PlainLink | Array<PlainLink | null | undefined> | null;
  mode?: string | null;
  sectionProps?: React.ComponentPropsWithoutRef<"section"> & SectionDataAttrs;
  text?: React.ReactNode;
  theme?: string | null;
};
export function HighlightHeadlineBlockView({
  anchor,
  hasScrollHighlighting = true,
  hasSolidBackground = false,
  hasTopDivider = true,
  headline,
  image,
  isBig,
  isHighlighted = false,
  link,
  mode = "light",
  sectionProps,
  text,
  theme = "white"
}: HighlightHeadlineBlockViewProps) {
  const cta = takeFirst(link);
  const icon = getPlainLinkIcon(cta);
  return <_StyledSection {...sectionProps} data-theme={mode as Mode} id={takeFirst(anchor)?.id || undefined} data-anchor-name={takeFirst(anchor)?.name || undefined} $_css={[{
    "color": "var(--text-secondary)"
  }, theme === "grey" ? {
    "backgroundColor": "var(--bg-tertiary)"
  } : {
    "backgroundColor": "var(--bg-primary)"
  }, hasTopDivider && {
    "borderTopWidth": "1px",
    "borderColor": "var(--stroke-primary)"
  }, hasSolidBackground && {
    "position": "relative"
  }, theme === "grey" && {
    "backgroundColor": "var(--bg-tertiary)"
  }, theme === "grey" && mode === "dark" && {
    "backgroundColor": "var(--bg-primary)"
  }, isHighlighted && {
    "backgroundColor": "var(--white-static)",
    "color": "var(--black-static)"
  }]}>
      <_StyledWrap>
        <_StyledDiv $_css2={[{
        "display": "flex",
        "flexDirection": "column",
        "alignItems": "center",
        "justifyContent": "center",
        "rowGap": "var(--spacing-xl)",
        "paddingLeft": "var(--spacing-lg)",
        "paddingRight": "var(--spacing-lg)",
        "paddingTop": "var(--spacing-3xl)",
        "paddingBottom": "var(--spacing-3xl)",
        "textAlign": "center",
        "@media (min-width: 1024px)": {
          "rowGap": "var(--spacing-lg)",
          "padding": "var(--spacing-4xl)"
        }
      }, isBig && {
        "@media (min-width: 1024px)": {
          "padding": "23.2rem"
        }
      }]}>
          {image && <Asset asset={image as AssetProps["asset"]} imageProps={{
          rounded: false,
          fit: "contain"
        }} />}

          {headline && <>
              {hasScrollHighlighting ? <ScrollHighlightText text={headline} /> : <_StyledHeadline type="h2" text={headline} $_css3={[{
            "maxWidth": "128rem",
            "fontSize": "2.4rem",
            "lineHeight": "1.2",
            "fontWeight": "400",
            "@media (min-width: 1024px)": {
              "fontSize": "3.8rem",
              "lineHeight": "1.3",
              "letterSpacing": "0.038rem",
              "fontWeight": "400"
            }
          }]} />}
            </>}

          {text && <_StyledDiv2>{text}</_StyledDiv2>}

          {isLinkValid(cta) && <Button as={Link} {...cta} icon={icon} variant="underline" />}
        </_StyledDiv>
      </_StyledWrap>
    </_StyledSection>;
}
var _StyledSection = /*#__PURE__*/_styled("section").withConfig({
  componentId: "sc-sf-1co45h2-0"
})`${p => p.$_css}`;
var _StyledWrap = _styled2(Wrap).withConfig({
  componentId: "sc-sf-1co45h2-1"
})({
  "borderWidth": "0px"
});
var _StyledDiv = _styled3("div").withConfig({
  componentId: "sc-sf-1co45h2-2"
})`${p => p.$_css2}`;
var _StyledHeadline = _styled4(Headline).withConfig({
  componentId: "sc-sf-1co45h2-3"
})`${p => p.$_css3}`;
var _StyledDiv2 = /*#__PURE__*/_styled5("div").withConfig({
  displayName: "highlight-headline___StyledDiv2",
  componentId: "sc-19wis7i-0"
})({
  "maxWidth": "60ch",
  "color": "rgba(0, 7, 4, 0.65)"
});